// TeamRoleSection — one role row in the Team Detail view (PRODOPSAI-543,
// replacing the category swimlanes from PRODOPSAI-534). One collapsible
// section per PDF team role (Engineering Manager / Lead Engineer /
// Engineer / Front-end Engineer / Solution Architect / …) with member
// cards: initials + name + a small role badge. The Entra job title is
// rendered as a secondary line ONLY when it differs from the role —
// the role itself is implied by the section header.
//
// Departed members render greyed with a "Departed …" tooltip.

import { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, Tooltip } from '@/components/ui';
import { cn } from '@/lib/utils';
import { personDisplayProps } from '@/components/admin/team-role-tree-helpers';
import { initials } from './team-card-helpers';
import { roleAccent } from './team-identity';
import { shouldShowEntraTitle, type RoleSection, type SectionMember } from './team-detail-helpers';

interface MemberRowProps {
  member: SectionMember;
  /** Whether the parent section is sourced from the catalog or Entra title. */
  sectionSource: RoleSection['source'];
  abbrev: string;
  badgeBg: string;
  badgeText: string;
  roleBadgeClass: string;
}

// Module-level so it is not redefined per render (S7721).
function MemberRow({
  member,
  sectionSource,
  abbrev,
  badgeBg,
  badgeText,
  roleBadgeClass,
}: Readonly<MemberRowProps>) {
  const { person } = member;
  const display = personDisplayProps(person);
  const showEntraTitle = shouldShowEntraTitle({ source: sectionSource }, member);
  const hasEmail = person.email !== null && person.email.trim() !== '';
  return (
    <li className="flex items-start gap-3 rounded-lg p-2 hover:bg-muted/40" data-testid="member-card">
      <span
        className={cn(
          'flex h-9 w-9 flex-none items-center justify-center rounded-full text-xs font-semibold',
          person.isActive ? cn(badgeBg, badgeText) : 'bg-muted/50 text-muted-foreground/50',
        )}
        aria-hidden="true"
      >
        {initials(person.displayName)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Tooltip
            content={display.tooltipContent ?? ''}
            disabled={display.tooltipContent === null}
            side="top"
          >
            <span className={cn('truncate text-sm font-medium', display.nameClassName)}>
              {person.displayName}
            </span>
          </Tooltip>
          <span
            className={cn('flex-none rounded px-1 py-0.5 text-[10px] font-semibold', roleBadgeClass)}
            title={member.roleDisplayName}
          >
            {abbrev}
          </span>
        </div>
        {showEntraTitle && (
          <p className="truncate text-xs text-muted-foreground" data-testid="entra-title">
            {person.jobTitle}
          </p>
        )}
        {hasEmail && (
          <p className="truncate text-xs text-muted-foreground/80">{person.email}</p>
        )}
      </div>
    </li>
  );
}

interface TeamRoleSectionProps {
  section: RoleSection;
  /** Team accent for member initials (cohesive within a team). */
  badgeBg: string;
  badgeText: string;
  defaultOpen?: boolean;
}

export function TeamRoleSection({
  section,
  badgeBg,
  badgeText,
  defaultOpen = true,
}: Readonly<TeamRoleSectionProps>) {
  const [open, setOpen] = useState(defaultOpen);
  const accent = roleAccent(section.id);
  const roleBadgeClass = cn(accent.tint, accent.text);

  return (
    <section
      className="flex min-w-0 flex-col rounded-xl border border-border bg-card"
      data-testid="role-section"
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => prev === false)}
        className={cn('flex items-center gap-2 rounded-t-xl px-3 py-2 text-left', accent.tint)}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDownIcon className="h-4 w-4 flex-none text-muted-foreground" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 flex-none text-muted-foreground" />
        )}
        <span className={cn('text-sm font-semibold', accent.text)}>{section.label}</span>
        <span className="text-xs text-muted-foreground">{section.members.length}</span>
      </button>
      {open && (
        <ul className="flex flex-col gap-1 p-2">
          {section.members.map((member) => (
            <MemberRow
              key={member.person.membershipId}
              member={member}
              sectionSource={section.source}
              abbrev={section.abbrev}
              badgeBg={badgeBg}
              badgeText={badgeText}
              roleBadgeClass={roleBadgeClass}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
